import { axiosClient, buildQuery } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import {
  ActivityStreamResponse,
  NotificationActivityRecord,
  NotificationComment,
  NotificationMention,
  NotificationNote,
  UserAvatarEnrichment,
  UserEnrichment,
} from '@nx-frontend/shared/types';

interface ActivityPortalFilter {
  $or?: (
    | {
        [key: string]: {
          $notIn: string[];
        };
      }
    | {
        [key: string]: null;
      }
  )[];
}

export type ActivityFilterValue =
  | {
      [key: string]:
        | null
        | string
        | string[]
        | number
        | number[]
        | { $notIn: string[] | number[] }
        | { $in: string[] | number[] }
        | { $or: unknown };
    }
  | ActivityPortalFilter;
export type ActivityFilterType = ActivityFilterValue | { $or: ActivityFilterValue[] };

interface GetActivitiesParams {
  hostUrl?: string;
  workspaceId: string;
  sessionId: string;
  limit?: number;
  beforeId?: number | undefined;
  unread?: boolean;
  filter?: ActivityFilterType;
}

export async function getActivities({
  hostUrl,
  workspaceId,
  sessionId,
  limit,
  beforeId,
  unread,
  filter,
}: GetActivitiesParams): Promise<ActivityStreamResponse> {
  const filterStr: string | null = filter ? JSON.stringify(filter) : null;

  const queryParams = buildQuery({
    limit,
    beforeId,
    unread,
    filter: filterStr,
  });
  const query = queryParams ? `?${queryParams}` : '';

  try {
    const response = await axiosClient.get<ActivityStreamResponse>(
      `${hostUrl ? hostUrl : generalEnv.gatewayUrl}/svc:notification/workspaces/${workspaceId}/activityStream${query}`,
      {
        headers: {
          'everhelper-session-id': `${sessionId}`,
        },
      }
    );

    return response.data;
  } catch (err) {
    console.error(err);
  }

  const initialActivitiesState: ActivityStreamResponse = {
    activityStream: [] as NotificationActivityRecord[],
    notes: [] as NotificationNote[],
    noteParents: [],
    comments: [] as NotificationComment[],
    mentions: [] as NotificationMention[],
    users: [] as UserEnrichment[],
    avatars: [] as UserAvatarEnrichment[],
  };

  return initialActivitiesState;
}

interface ReadActivityParams {
  hostUrl?: string;
  workspaceId: string;
  sessionId: string;
  activityId: string;
  read: boolean;
}

export async function readActivity(params: ReadActivityParams): Promise<void> {
  const { hostUrl, workspaceId, sessionId, activityId, read } = params;

  try {
    await axiosClient.post(
      `${hostUrl}/ft:workspaceActivityStream/workspaces/${workspaceId}/activityStream/${activityId}/read`,
      {
        state: read,
      },
      {
        headers: {
          'everhelper-session-id': `${sessionId}`,
        },
      }
    );
  } catch (err) {
    console.error(err);
  }
}

interface ReadAllActivityParams {
  hostUrl?: string;
  workspaceId: string;
  sessionId: string;
}

export async function readAllActivity(params: ReadAllActivityParams): Promise<void> {
  const { hostUrl, workspaceId, sessionId } = params;

  try {
    await axiosClient.post(
      `${hostUrl}/ft:workspaceActivityStream/workspaces/${workspaceId}/activityStream/readAll`,
      [],
      {
        headers: {
          'everhelper-session-id': `${sessionId}`,
        },
      }
    );
  } catch (err) {
    console.error(err);
  }
}
